import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import _includes from 'lodash/includes';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import { columns } from './TableRatesMetadata';
import classnames from 'classnames';
import InfoIcon from '@material-ui/icons/Info';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import { AppContext } from '../../../AppProvider';

import TableRateDetails from './TableRateDetails';

import {
  SortingState,
  IntegratedSorting,
  RowDetailState,
  PagingState,
  IntegratedPaging
} from '@devexpress/dx-react-grid';
import {
  Grid,
  Table,
  TableHeaderRow,
  TableRowDetail,
  PagingPanel
} from '@devexpress/dx-react-grid-material-ui';

import Radio from '@material-ui/core/Radio';

import combineStyles from '../../../utils/combineStyles';
import TypographyStyles from '../../../styles/typography';
import SpacingStyles from '../../../styles/helpers/spacing';
import TableRatesStyles from './TableRates.styles';

const formatErrorTooltip = messages => {
  if (_isEmpty(messages)) {
    // hopefully should happen but just in case
    return 'N/A';
  }
  // TODO - look for more than just the first one?
  return messages[0].message;
};

const formatLeadTimeTooltip = (leadTimeWindowMinutes, dockCloseTime) => {
  const hours = leadTimeWindowMinutes / 60;
  return `Carrier requires ${hours.toFixed(2)} ${
    hours > 1 || hours < 1 ? ' hours' : ' hour'
  } before your dock closing time${dockCloseTime ? ' of ' : ''}${
    dockCloseTime ? dockCloseTime : ''
  }.`;
};

const getCell = (props, classes, t) => {
  const { column, row } = props;
  const showError = !row.canBeSelected;
  let errorTooltip = '';
  if (showError) {
    errorTooltip = formatErrorTooltip(row.messages);
  }
  const leadTimeTooltip = formatLeadTimeTooltip(
    row.leadTimeWindowMinutes,
    row.dockCloseTime
  );
  if (column.name === 'rate') {
    return (
      <Table.Cell
        className={classnames(classes.tableCell, classes.body2)}
        {...props}
      >
        {_get(row, 'prettyRate', '--') || '--'}
        {!showError && (row.accessorial || row.transit || row.fuel) && (
          <Tooltip
            title={
              <div>
                {row.accessorial && (
                  <p className={classes.noMargin}>
                    {t('Accessorial')}: {row.accessorial}
                  </p>
                )}
                {row.transit && (
                  <p className={classes.noMargin}>
                    {t('Transit')}: {row.transit}
                  </p>
                )}
                {row.fuel && (
                  <p className={classes.noMargin}>
                    {t('Fuel')}: {row.fuel}
                  </p>
                )}
              </div>
            }
            classes={{
              tooltip: classnames(classes.caption, classes.tooltip),
              popper: classes.tooltipPopper
            }}
            placement="top-start"
          >
            <IconButton className={classes.tooltipButton}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        )}
        {showError && (
          <Tooltip
            title={<div>{errorTooltip}</div>}
            classes={{
              tooltip: classnames(classes.caption, classes.tooltip),
              popper: classes.tooltipPopper
            }}
            placement="top-start"
          >
            <IconButton className={classes.tooltipButton}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        )}
      </Table.Cell>
    );
  }

  if (column.name === 'shipToday') {
    return (
      <Table.Cell
        className={classnames(classes.tableCell, classes.body2)}
        {...props}
      >
        {_get(row, 'shipToday', '--') || '--'}
        {row.canBeSelected && (
          <Tooltip
            title={<div>{leadTimeTooltip}</div>}
            classes={{
              tooltip: classnames(classes.caption, classes.tooltip),
              popper: classes.tooltipPopper
            }}
            placement="top-start"
          >
            <IconButton className={classes.tooltipButtonShipToday}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        )}
      </Table.Cell>
    );
  }
  return (
    <Table.Cell
      className={classnames(classes.tableCell, classes.body2)}
      {...props}
    >
      {_get(row, column.name) || column.name === 'type' ? '' : '--'}
    </Table.Cell>
  );
};

export class TableRates extends Component {
  static propTypes = {
    values: PropTypes.object.isRequired,
    isQuickRate: PropTypes.bool,
    selectedRateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    expandedRowIds: PropTypes.array,
    rows: PropTypes.array.isRequired,
    currentPage: PropTypes.number.isRequired,
    handlePageChange: PropTypes.func.isRequired,
    pageSize: PropTypes.number.isRequired,
    handlePageSizeChange: PropTypes.func.isRequired,
    handleValidateField: PropTypes.func
  };
  state = {
    pageSizes: [5, 10, 15]
  };

  render() {
    const {
      classes,
      selectedRateId,
      expandedRowIds,
      onExpandedRowIdsChange,
      t,
      rows,
      values,
      isQuickRate,
      handlePageChange,
      handlePageSizeChange,
      currentPage,
      pageSize,
      handleValidateField
    } = this.props;

    const { pageSizes } = this.state;

    return (
      <Grid rows={rows} columns={columns({ t })}>
        <SortingState />
        <IntegratedSorting />
        <PagingState
          currentPage={currentPage}
          onCurrentPageChange={handlePageChange}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
        />
        <IntegratedPaging />
        <RowDetailState
          onExpandedRowIdsChange={onExpandedRowIdsChange}
          expandedRowIds={expandedRowIds}
        />
        <Table
          tableComponent={props => (
            <Table.Table {...props} className={classes.table} />
          )}
          rowComponent={props => (
            <Table.Row
              className={classnames(classes.tableRow, {
                [!isQuickRate ? classes.rowSelected : '']: _includes(
                  expandedRowIds,
                  props.tableRow.rowId
                )
              })}
              {...props}
              onClick={() =>
                onExpandedRowIdsChange([props.tableRow.rowId], props.row)
              }
            />
          )}
          cellComponent={props => getCell(props, classes, t)}
        />
        <PagingPanel
          pageSizes={pageSizes}
          containerComponent={props => (
            <PagingPanel.Container {...props} className={classes.pagingPanel} />
          )}
        />
        <TableHeaderRow
          showSortingControls
          rowComponent={props => (
            <TableHeaderRow.Row {...props} className={classes.tableHeaderRow} />
          )}
          cellComponent={props => {
            return (
              <TableHeaderRow.Cell
                className={classnames(classes.tableCellHeader, classes.caption)}
                {...props}
              />
            );
          }}
        />
        {!isQuickRate && (
          <TableRowDetail
            contentComponent={props => (
              <TableRateDetails
                {...props}
                handleValidateField={handleValidateField}
                values={values}
                context={this.context}
                isQuickRate={isQuickRate}
              />
            )}
            rowComponent={props => (
              <TableRowDetail.Row
                {...props}
                className={classes.tableRowDetailRow}
              />
            )}
            toggleCellComponent={({ expanded, ...props }) => {
              const { row } = props;
              return (
                <Table.Cell
                  expanded={`${expanded}`}
                  {...props}
                  className={classnames(
                    classes.selectRateRadioButton,
                    classes.tableCell
                  )}
                >
                  {row.canBeSelected && (
                    <Radio
                      checked={
                        selectedRateId &&
                        (row.rateItemGuid === selectedRateId ||
                          row.customRateItemGuid === selectedRateId)
                      }
                      onChange={() => {}}
                      value="b"
                      name="radio-button-demo"
                      classes={{
                        root: classes.radioButtonRoot,
                        checked: classes.radioButtonChecked
                      }}
                    />
                  )}
                </Table.Cell>
              );
            }}
          />
        )}
      </Grid>
    );
  }
}

TableRates.contextType = AppContext;
const TableRatesWithTranslations = withNamespaces()(TableRates);
export default withStyles(
  combineStyles(TableRatesStyles, TypographyStyles, SpacingStyles)
)(TableRatesWithTranslations);
